/*
 * RSVP Digest Reports — scheduled RSVP snapshots delivered to a small
 * set of trusted people around the couple. Backed by /api/rsvp-digest/*.
 *
 * Owns:
 *   - Timezone gate (feature disabled until profile.timezone is set)
 *   - Enabled toggle
 *   - Recipient list + editor (channels/cadence/hour/weekday)
 *   - Country-coverage inline warnings (Quo blocked list)
 *   - Editable email + SMS body templates with merge fields
 *   - Test send buttons for both channels
 *   - Last-10 send history per recipient
 */
import React, { useEffect, useState, useMemo } from 'react';
import SmsConsentCheckbox from '../../components/compliance/SmsConsentCheckbox';
import { Send, Bell, Clock, Mail, MessageSquare, Trash2, RefreshCw, Copy, Link2, AlertTriangle, ChevronDown, ChevronRight, ChevronLeft, Eye, Lock } from 'lucide-react';
import client from '../../api/client';
import InfoTip from '../../components/InfoTip';

const CADENCES = [
  { value: 'hourly',         label: 'Every hour'    },
  { value: 'every_2_hours',  label: 'Every 2 hours' },
  { value: 'every_3_hours',  label: 'Every 3 hours' },
  { value: 'every_4_hours',  label: 'Every 4 hours' },
  { value: 'every_6_hours',  label: 'Every 6 hours' },
  { value: 'every_8_hours',  label: 'Every 8 hours' },
  { value: 'every_12_hours', label: 'Every 12 hours' },
  { value: 'daily',          label: 'Every day'     },
  { value: 'weekly',         label: 'Once a week'   },
];
// Sub-daily cadences accept a quiet-hours window ("don't send too early
// or too late"). Daily/weekly already fire at a single moment so they
// don't need one.
const SUB_DAILY = new Set(['hourly', 'every_2_hours', 'every_3_hours', 'every_4_hours', 'every_6_hours', 'every_8_hours', 'every_12_hours']);

// Mirror of the backend `_within_quiet_window` — used to warn the couple
// when a daily/weekly send would silently fall outside their quiet window.
function hourInsideWindow(hr, start, end) {
  if (start === null || start === undefined || end === null || end === undefined) return true;
  start = ((start % 24) + 24) % 24;
  end = ((end % 24) + 24) % 24;
  if (start === end) return hr === start;
  if (start < end) return hr >= start && hr <= end;
  return hr >= start || hr <= end;   // wraps midnight
}
const HOURS = Array.from({ length: 24 }, (_, i) => ({ value: i, label: `${((i + 11) % 12) + 1}${i < 12 ? ' AM' : ' PM'}` }));
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MERGE_TIPS = '{coupleNames} · {recipientName} · {pending} · {attending} · {declined} · {headcount} · {total} · {weddingDate} · {venue} · {reportUrl}';

const emptyDraft = () => ({
  name: '', email: '', phone: '', channels: ['email'],
  cadence: 'daily', hour: 9, weekday: 0, enabled: true,
  quietStartHour: 8, quietEndHour: 21,  // sensible default: 8 AM–9 PM
  smsConsentConfirmed: false, consentOnFilePhone: null,
});

export default function DigestReportsEditor({ toast, goToProfile }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [editingId, setEditingId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewMode, setPreviewMode] = useState('web');   // 'web' | 'email'
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const loadState = async () => {
    try {
      const { data } = await client.get('/rsvp-digest/state');
      setState(data);
    } catch { /* noop */ }
    setLoading(false);
  };
  const loadPreview = async (mode = previewMode) => {
    setPreviewLoading(true);
    try {
      const url = mode === 'email' ? '/rsvp-digest/preview-email.html' : '/rsvp-digest/preview.html';
      const { data } = await client.get(url, { responseType: 'text' });
      setPreviewHtml(data || '');
    } catch { setPreviewHtml('<p style="padding:24px;font-family:sans-serif;color:#9A8A7E">Preview unavailable — save a change or reload the page.</p>'); }
    setPreviewLoading(false);
  };
  useEffect(() => { loadState(); loadPreview('web'); /* eslint-disable-next-line */ }, []);

  const patch = (obj) => setDraft((d) => ({ ...d, ...obj }));

  const saveConfig = async (partial) => {
    setSaving(true);
    try {
      await client.put('/rsvp-digest/config', { ...state.config, ...partial });
      setState((s) => ({ ...s, config: { ...s.config, ...partial } }));
      toast?.({ title: 'Saved' });
    } finally { setSaving(false); }
  };

  const addOrUpdate = async () => {
    if (!draft.name.trim()) { toast?.({ title: 'Name required' }); return; }
    setSaving(true);
    try {
      if (editingId) {
        const { data } = await client.put(`/rsvp-digest/recipients/${editingId}`, draft);
        setState((s) => ({ ...s, recipients: s.recipients.map((r) => r.id === editingId ? data : r) }));
      } else {
        const { data } = await client.post('/rsvp-digest/recipients', draft);
        setState((s) => ({ ...s, recipients: [...s.recipients, data] }));
      }
      setDraft(emptyDraft()); setEditingId(null);
      toast?.({ title: editingId ? 'Recipient updated' : 'Recipient added' });
      loadPreview(previewMode);
    } catch (e) {
      toast?.({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  const removeRecipient = async (id) => {
    if (!window.confirm('Remove this recipient? Their private report link stops working immediately.')) return;
    await client.delete(`/rsvp-digest/recipients/${id}`);
    setState((s) => ({ ...s, recipients: s.recipients.filter((r) => r.id !== id) }));
    loadPreview(previewMode);
  };

  const editRecipient = (r) => {
    setEditingId(r.id);
    setDraft({
      name: r.name || '', email: r.email || '', phone: r.phone || '',
      channels: r.channels || ['email'], cadence: r.cadence || 'daily',
      hour: r.hour ?? 9, weekday: r.weekday ?? 0, enabled: r.enabled !== false,
      quietStartHour: r.quietStartHour ?? 8,
      quietEndHour: r.quietEndHour ?? 21,
      smsConsentConfirmed: false, consentOnFilePhone: r.sms_consent?.phone || null,
    });
  };

  const rotateLink = async (id) => {
    const { data } = await client.post(`/rsvp-digest/recipients/${id}/rotate`);
    setState((s) => ({ ...s, recipients: s.recipients.map((r) => r.id === id ? data : r) }));
    toast?.({ title: 'Link rotated', description: 'The old URL no longer works.' });
  };

  if (loading) {
    return (
      <section data-testid="digest-editor" className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
        <p className="text-sm text-[#9A8A7E]">Loading…</p>
      </section>
    );
  }

  const tzOK = state?.timezone_valid;

  return (
    <div className="relative" data-testid="digest-editor-wrap">
    <section data-testid="digest-editor" className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-6 space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
          <Bell className="w-4 h-4" /> RSVP digest reports
          <InfoTip testId="info-digest">
            <p>Send a scheduled RSVP snapshot by email and/or text to the small circle around you — mom, planner, siblings, bridal-party leads. Each recipient picks their own cadence.</p>
            <p>Everyone gets their own private web dashboard with drill-down guest lists (no login).</p>
          </InfoTip>
        </h3>
        <div className="flex items-center gap-4">
        <button
          data-testid="digest-preview-toggle"
          onClick={() => { setPreviewOpen(true); loadPreview(previewMode); }}
          className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white transition-colors"
        ><Eye className="w-4 h-4" /> Live preview <ChevronLeft className="w-4 h-4" /></button>
        {tzOK && (
          <label className="inline-flex items-center gap-2 text-sm text-[#3A3A3A]">
            <input
              data-testid="digest-enabled"
              type="checkbox"
              checked={state.config.enabled}
              onChange={(e) => saveConfig({ enabled: e.target.checked })}
              disabled={saving}
              className="accent-[#7B2C3B]"
            /> Enabled
          </label>
        )}
        </div>
      </div>

      {state?.schedule?.status === 'closed' && (
        <div data-testid="digest-schedule-closed" className="rounded-xl bg-[#EAF2E5] border border-[#C9DDBB] p-3 text-sm text-[#2F5D2F] flex items-start gap-2">
          <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>RSVPs closed on {state.schedule.reply_by}. The final headcount report went out{state.schedule.closed_at ? ` on ${new Date(state.schedule.closed_at).toLocaleString()}` : ''}, and the digest schedule has stopped. Move your reply-by date later to restart it.</span>
        </div>
      )}
      {state?.schedule?.status === 'final_pending' && (
        <div data-testid="digest-schedule-final-pending" className="rounded-xl bg-[#FBF1EA] border border-[#EFC9B0] p-3 text-sm text-[#7A3E1A]">
          Your reply-by date ({state.schedule.reply_by}) has passed. Each recipient gets one final headcount report (after auto-decline runs, inside their send hours), and then the schedule stops.
        </div>
      )}
      {state?.schedule?.status === 'active' && state.schedule.reply_by && (
        <p data-testid="digest-schedule-active" className="text-xs text-[#6A6A6A]">Digests run until your reply-by date ({state.schedule.reply_by}). The day after, everyone gets a final headcount and the schedule stops.</p>
      )}

      {!tzOK ? (
        <div data-testid="digest-tz-gate" className="rounded-2xl border border-[#B8896C] bg-[#F4E4DA] p-4">
          <p className="font-serif text-lg text-[#7B2C3B] mb-1">Pick your timezone to enable digest reports</p>
          <p className="text-sm text-[#6A6A6A] mb-3">
            Digests fire at each recipient's chosen hour <em>in your local time</em>. Without a timezone we can't schedule
            anything, so this whole section stays paused.
          </p>
          <button
            data-testid="digest-tz-goto"
            onClick={goToProfile}
            className="text-sm px-4 py-2 rounded-full bg-[#7B2C3B] text-white hover:bg-[#611E2E]"
          >Set your timezone in Profile →</button>
        </div>
      ) : (
        <>
          {/* Recipient list */}
          <div className="space-y-2">
            {state.recipients.length === 0 && (
              <p className="text-sm text-[#9A8A7E] py-3">No recipients yet. Add someone below to start their digest schedule.</p>
            )}
            {state.recipients.map((r) => (
              <RecipientRow
                key={r.id}
                recipient={r}
                onEdit={() => editRecipient(r)}
                onDelete={() => removeRecipient(r.id)}
                onRotate={() => rotateLink(r.id)}
                onTest={loadState}
                toast={toast}
              />
            ))}
          </div>

          {/* Draft form */}
          <div className="rounded-2xl border border-dashed border-[#EFE3D8] p-4 bg-[#FBF7F2] space-y-3">
            <p className="text-xs uppercase tracking-wider text-[#7B2C3B] font-medium">
              {editingId ? 'Edit recipient' : 'Add a recipient'}
            </p>
            <RecipientDraftForm draft={draft} patch={patch} />
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="digest-add-recipient"
                onClick={addOrUpdate}
                disabled={saving || !draft.name.trim()}
                className="text-sm px-5 py-2 rounded-full bg-[#7B2C3B] text-white hover:bg-[#611E2E] disabled:opacity-50"
              >{editingId ? 'Save changes' : 'Add recipient'}</button>
              {editingId && (
                <button
                  onClick={() => { setEditingId(null); setDraft(emptyDraft()); }}
                  className="text-sm px-4 py-2 rounded-full border border-[#EFE3D8] text-[#3A3A3A]"
                >Cancel</button>
              )}
            </div>
          </div>

          {/* Templates */}
          <details className="rounded-2xl border border-[#EFE3D8] p-4 bg-white">
            <summary className="cursor-pointer text-sm font-medium text-[#2A2A2A] inline-flex items-center gap-2">
              <Mail className="w-4 h-4" /> Message templates
              <span className="text-[10px] text-[#9A8A7E]">(optional — good defaults ship out of the box)</span>
            </summary>
            <div className="mt-3 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#7B2C3B]">Email subject</label>
                <input
                  data-testid="digest-email-subject"
                  className="w-full px-3 py-2 mt-1 rounded-full border border-[#EFE3D8] text-sm"
                  value={state.config.emailSubject}
                  onChange={(e) => setState((s) => ({ ...s, config: { ...s.config, emailSubject: e.target.value } }))}
                  onBlur={(e) => saveConfig({ emailSubject: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#7B2C3B]">Email intro</label>
                <textarea
                  data-testid="digest-email-intro"
                  rows={2}
                  className="w-full px-3 py-2 mt-1 rounded-xl border border-[#EFE3D8] text-sm"
                  value={state.config.emailIntro}
                  onChange={(e) => setState((s) => ({ ...s, config: { ...s.config, emailIntro: e.target.value } }))}
                  onBlur={(e) => saveConfig({ emailIntro: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#7B2C3B]">SMS body</label>
                <textarea
                  data-testid="digest-sms-template"
                  rows={3}
                  className="w-full px-3 py-2 mt-1 rounded-xl border border-[#EFE3D8] text-sm"
                  value={state.config.smsTemplate}
                  onChange={(e) => setState((s) => ({ ...s, config: { ...s.config, smsTemplate: e.target.value } }))}
                  onBlur={(e) => saveConfig({ smsTemplate: e.target.value })}
                />
              </div>
              <p className="text-[11px] text-[#9A8A7E]">Merge fields: {MERGE_TIPS}</p>
            </div>
          </details>

          <p className="text-[11px] text-[#9A8A7E]">
            International SMS: two-way replies work in ~20 countries; some countries are send-only or blocked entirely.
          </p>
        </>
      )}
    </section>

    {/* Live preview — slide-out drawer on the right, opened on demand. */}
    {previewOpen && <div data-testid="digest-preview-backdrop" className="fixed inset-0 z-[60] bg-[#2A1F1B]/30 backdrop-blur-[2px]" onClick={() => setPreviewOpen(false)} />}
    {previewOpen && (
    <aside data-testid="digest-preview"
           className="fixed top-0 right-0 z-[70] h-full w-full sm:w-[680px] bg-white border-l border-[#EFE3D8] shadow-2xl p-5 flex flex-col gap-3 animate-in slide-in-from-right duration-300">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button data-testid="digest-preview-close" onClick={() => setPreviewOpen(false)} className="w-8 h-8 rounded-full border border-[#EFE3D8] flex items-center justify-center hover:border-[#7B2C3B]" title="Hide preview">
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="text-xs uppercase tracking-widest text-[#7B2C3B] font-medium">Live preview</div>
        </div>
        <div className="inline-flex items-center gap-1 text-[11px]">
          <button
            data-testid="digest-preview-mode-web"
            onClick={() => { setPreviewMode('web'); loadPreview('web'); }}
            className={`px-2 py-1 rounded-full border ${previewMode === 'web' ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'border-[#EFE3D8] text-[#3A3A3A]'}`}
          >Web report</button>
          <button
            data-testid="digest-preview-mode-email"
            onClick={() => { setPreviewMode('email'); loadPreview('email'); }}
            className={`px-2 py-1 rounded-full border ${previewMode === 'email' ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'border-[#EFE3D8] text-[#3A3A3A]'}`}
          >Email</button>
          <button
            data-testid="digest-preview-refresh"
            onClick={() => loadPreview(previewMode)}
            className="ml-1 px-2 py-1 rounded-full border border-[#EFE3D8] text-[#3A3A3A] inline-flex items-center gap-1"
            title="Refresh preview"
          ><RefreshCw className="w-3 h-3" /> Refresh</button>
        </div>
      </div>
      <div className="rounded-xl overflow-hidden border border-[#EFE3D8] bg-[#FBF7F2] flex-1 min-h-0">
        {previewLoading ? (
          <div className="w-full h-full flex items-center justify-center text-xs text-[#9A8A7E]">Loading preview…</div>
        ) : (
          <iframe
            title="RSVP digest preview"
            data-testid="digest-preview-iframe"
            srcDoc={previewHtml}
            sandbox="allow-same-origin"
            className="w-full h-full border-0"
          />
        )}
      </div>
      <p className="text-[11px] text-[#9A8A7E]">
        This is exactly what your recipients will see — click <em>Refresh</em> after changes on the guest list or template.
      </p>
    </aside>
    )}
    </div>
  );
}

// -----------------------------------------------------------------------

function RecipientDraftForm({ draft, patch }) {
  // Live coverage preview from the phone the user is typing.
  const cov = usePhoneCoverage(draft.phone);
  const forcedSmsOff = draft.channels.includes('sms') && cov?.blocked;
  useEffect(() => {
    if (forcedSmsOff) {
      patch({ channels: draft.channels.filter((c) => c !== 'sms') });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedSmsOff]);

  const toggleChan = (c) => {
    const set = new Set(draft.channels);
    if (set.has(c)) set.delete(c); else set.add(c);
    patch({ channels: Array.from(set) });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input data-testid="draft-name" className="px-3 py-2 rounded-full border border-[#EFE3D8] text-sm" placeholder="Name" value={draft.name} onChange={(e) => patch({ name: e.target.value })} />
        <input data-testid="draft-email" className="px-3 py-2 rounded-full border border-[#EFE3D8] text-sm" placeholder="Email (optional)" value={draft.email} onChange={(e) => patch({ email: e.target.value })} />
        <input data-testid="draft-phone" className="px-3 py-2 rounded-full border border-[#EFE3D8] text-sm" placeholder="Phone with country code" value={draft.phone} onChange={(e) => patch({ phone: e.target.value })} />
      </div>
      {cov && cov.country_name && (
        <div className="flex items-center gap-2 flex-wrap">
          <span data-testid="draft-country-pill" className="text-[11px] px-2 py-1 rounded-full bg-white border border-[#EFE3D8] inline-flex items-center gap-1">
            <span aria-hidden="true">{cov.flag}</span> {cov.country_name}
          </span>
          {cov.blocked ? (
            <span className="text-[11px] px-2 py-1 rounded-full bg-red-50 text-red-700 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> SMS not supported
            </span>
          ) : cov.two_way ? (
            <span className="text-[11px] px-2 py-1 rounded-full bg-[#E6F1E6] text-[#2F5D2F]">Replies enabled</span>
          ) : (
            <span className="text-[11px] px-2 py-1 rounded-full bg-[#FBF1E6] text-[#A15A2A]">Send only</span>
          )}
          {cov.reason && <span className="text-[11px] text-[#9A8A7E]">{cov.reason}</span>}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span className="text-[11px] uppercase tracking-wider text-[#7B2C3B] mr-1">Channels</span>
        <label className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border cursor-pointer ${draft.channels.includes('email') ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8]'}`}>
          <input data-testid="draft-chan-email" type="checkbox" className="hidden" checked={draft.channels.includes('email')} onChange={() => toggleChan('email')} />
          <Mail className="w-3 h-3" /> Email
        </label>
        <label className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border cursor-pointer ${draft.channels.includes('sms') ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8]'} ${cov?.blocked ? 'opacity-40 cursor-not-allowed' : ''}`}>
          <input data-testid="draft-chan-sms" type="checkbox" className="hidden" disabled={cov?.blocked} checked={draft.channels.includes('sms')} onChange={() => toggleChan('sms')} />
          <MessageSquare className="w-3 h-3" /> Text
        </label>
      </div>
      {draft.channels.includes('sms') && draft.phone && (
        draft.consentOnFilePhone && draft.consentOnFilePhone === draft.phone ? (
          <p data-testid="draft-sms-consent-on-file" className="text-[11px] text-[#2F5D2F]">Text consent on file for this number.</p>
        ) : (
          <SmsConsentCheckbox kind="digest_recipient" testId="draft-sms-consent" checked={draft.smsConsentConfirmed} onChange={(v) => patch({ smsConsentConfirmed: v })} />
        )
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <select data-testid="draft-cadence" className="px-3 py-2 rounded-full border border-[#EFE3D8] text-sm bg-white" value={draft.cadence} onChange={(e) => patch({ cadence: e.target.value })}>
          {CADENCES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select data-testid="draft-hour" className="px-3 py-2 rounded-full border border-[#EFE3D8] text-sm bg-white" value={draft.hour} onChange={(e) => patch({ hour: Number(e.target.value) })} title={SUB_DAILY.has(draft.cadence) ? 'Anchor hour — the recurring sends line up on this hour, e.g. every 4 hours starting at 9 AM = 9, 1, 5.' : 'What time to send'}>
          {HOURS.map((h) => (
            <option key={h.value} value={h.value}>{SUB_DAILY.has(draft.cadence) ? `Anchored @ ${h.label}` : h.label}</option>
          ))}
        </select>
        {draft.cadence === 'weekly' ? (
          <select data-testid="draft-weekday" className="px-3 py-2 rounded-full border border-[#EFE3D8] text-sm bg-white" value={draft.weekday} onChange={(e) => patch({ weekday: Number(e.target.value) })}>
            {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        ) : (
          <span className="px-3 py-2 text-xs text-[#9A8A7E] inline-flex items-center"><Clock className="w-3 h-3 mr-1" /> your timezone</span>
        )}
      </div>
      {/* Quiet-hours window — now shown on every cadence for form parity.
          Off by default for daily/weekly (where it usually adds nothing).
          When on for daily/weekly, we warn if the chosen hour falls outside
          the window because that combo would silently skip every send. */}
      <div className="mt-2 p-3 rounded-2xl bg-[#FBF7F2] border border-[#EFE3D8]">
        <label className="inline-flex items-center gap-2 text-xs text-[#3A3A3A] cursor-pointer">
          <input
            data-testid="draft-quiet-toggle"
            type="checkbox"
            className="accent-[#7B2C3B]"
            checked={draft.quietStartHour !== null && draft.quietEndHour !== null}
            onChange={(e) => patch({
              quietStartHour: e.target.checked ? (draft.quietStartHour ?? 8) : null,
              quietEndHour: e.target.checked ? (draft.quietEndHour ?? 21) : null,
            })}
          />
          <span className="font-medium">Quiet hours — only send between</span>
        </label>
        {(draft.quietStartHour !== null && draft.quietEndHour !== null) && (
          <>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <select
                data-testid="draft-quiet-start"
                className="px-3 py-1.5 rounded-full border border-[#EFE3D8] text-xs bg-white"
                value={draft.quietStartHour ?? 8}
                onChange={(e) => patch({ quietStartHour: Number(e.target.value) })}
              >
                {HOURS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
              </select>
              <span className="text-xs text-[#6A6A6A]">and</span>
              <select
                data-testid="draft-quiet-end"
                className="px-3 py-1.5 rounded-full border border-[#EFE3D8] text-xs bg-white"
                value={draft.quietEndHour ?? 21}
                onChange={(e) => patch({ quietEndHour: Number(e.target.value) })}
              >
                {HOURS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
              </select>
              <span className="text-[11px] text-[#9A8A7E]">in your timezone. Digests outside this window are skipped.</span>
            </div>
            {/* Warning: chosen hour never fires because it falls outside window */}
            {!SUB_DAILY.has(draft.cadence) && !hourInsideWindow(draft.hour, draft.quietStartHour, draft.quietEndHour) && (
              <p data-testid="draft-quiet-conflict" className="mt-2 text-[11px] text-[#A15A2A]">
                Heads up: your {draft.cadence === 'weekly' ? 'weekly' : 'daily'} send at {HOURS[draft.hour].label} falls outside your quiet-hours window — it would never fire. Move the hour or widen the window.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------

function RecipientRow({ recipient, onEdit, onDelete, onRotate, onTest, toast }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [testing, setTesting] = useState(false);
  const cov = recipient.coverage;
  const summary = useMemo(() => {
    const parts = [];
    if (recipient.email) parts.push('email');
    if (recipient.phone && recipient.channels?.includes('sms')) parts.push('text');
    return parts.length ? parts.join(' + ') : 'no channels';
  }, [recipient]);

  const doTest = async (which) => {
    setTesting(true);
    try {
      const body = which === 'email' ? { email: recipient.email } : { phone: recipient.phone };
      const { data } = await client.post(`/rsvp-digest/recipients/${recipient.id}/test`, body);
      toast?.({ title: 'Test send fired', description: JSON.stringify(data.result) });
      onTest?.();
    } catch (e) {
      toast?.({ title: 'Test send failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setTesting(false); }
  };

  const loadHistory = async () => {
    if (history) return;
    const { data } = await client.get(`/rsvp-digest/recipients/${recipient.id}/history`);
    setHistory(data.items || []);
  };
  useEffect(() => { if (open) loadHistory(); /* eslint-disable-next-line */ }, [open]);

  const reportUrl = `${window.location.origin}/api/report/${recipient.token}`;

  return (
    <div data-testid={`digest-recipient-${recipient.id}`} className="rounded-2xl border border-[#EFE3D8] bg-white overflow-hidden">
      <div className="p-3 flex items-center gap-3 flex-wrap">
        <button onClick={() => setOpen((o) => !o)} className="text-[#9A8A7E]" aria-label="Expand">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[#2A2A2A] font-medium truncate inline-flex items-center gap-1.5 flex-wrap">
            {recipient.name}
            {cov?.flag && <span title={cov.country_name} className="text-sm">{cov.flag}</span>}
            {cov?.blocked && (
              <span className="text-[9px] uppercase tracking-wider bg-red-50 text-red-700 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> SMS blocked
              </span>
            )}
            {!recipient.enabled && (
              <span className="text-[9px] uppercase tracking-wider bg-[#F4E4DA] text-[#7B2C3B] px-1.5 py-0.5 rounded-full">Paused</span>
            )}
          </p>
          <p className="text-[11px] text-[#9A8A7E] truncate">
            {summary} · {CADENCES.find((c) => c.value === recipient.cadence)?.label || recipient.cadence}
            {recipient.cadence === 'weekly' ? ` (${WEEKDAYS[recipient.weekday]})` : ''}
            {' @ '}{HOURS[recipient.hour]?.label} local
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid={`test-email-${recipient.id}`} disabled={!recipient.email || testing} onClick={() => doTest('email')} className="text-[11px] px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] disabled:opacity-40 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Test email</button>
          <button data-testid={`test-sms-${recipient.id}`} disabled={!recipient.phone || testing || !recipient.channels?.includes('sms')} onClick={() => doTest('sms')} className="text-[11px] px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] disabled:opacity-40 inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Test text</button>
          <button data-testid={`edit-${recipient.id}`} onClick={onEdit} className="text-[11px] px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:bg-[#FBF7F2]">Edit</button>
          <button data-testid={`delete-${recipient.id}`} onClick={onDelete} className="text-[11px] px-2.5 py-1.5 rounded-full border border-[#EFE3D8] text-red-600 hover:bg-red-50" title="Remove"><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>
      {open && (
        <div className="border-t border-[#EFE3D8] p-3 bg-[#FBF7F2] space-y-3">
          <div className="text-[11px] flex items-center gap-2 flex-wrap">
            <Link2 className="w-3 h-3 text-[#9A8A7E]" />
            <span className="text-[#6A6A6A]">Their private report link:</span>
            <code className="px-2 py-1 rounded bg-white border border-[#EFE3D8] text-[#3A3A3A] break-all">{reportUrl}</code>
            <button onClick={() => { navigator.clipboard.writeText(reportUrl); toast?.({ title: 'Link copied' }); }} className="text-[11px] px-2 py-1 rounded-full bg-[#F4E4DA] text-[#7B2C3B] inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
            <button onClick={onRotate} className="text-[11px] px-2 py-1 rounded-full bg-[#F4E4DA] text-[#7B2C3B] inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Rotate</button>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#7B2C3B] mb-1">Recent sends</p>
            {history === null ? (
              <p className="text-xs text-[#9A8A7E]">Loading history…</p>
            ) : history.length === 0 ? (
              <p className="text-xs text-[#9A8A7E]">No sends yet. Try a Test send above to see it appear here.</p>
            ) : (
              <ul className="text-xs text-[#3A3A3A] space-y-1">
                {history.map((h) => (
                  <li key={h.id} className="flex items-center justify-between border-b border-[#EFE3D8] py-1">
                    <span>{new Date(h.created_at).toLocaleString()} · <em className="text-[#9A8A7E]">{h.kind}</em></span>
                    <span className="text-[10px] text-[#6A6A6A]">
                      {h.result.email && <span className="mr-2">email: {h.result.email}</span>}
                      {h.result.sms && <span>sms: {h.result.sms}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function usePhoneCoverage(phone) {
  const [cov, setCov] = useState(null);
  useEffect(() => {
    // Client-side heuristic mirroring the backend's util so the UI feels
    // instant. Not authoritative — the backend validates on save.
    if (!phone) { setCov(null); return; }
    const digits = phone.replace(/\D/g, '');
    if (!digits) { setCov(null); return; }
    const BLOCKED = { 86:['🇨🇳','China'], 53:['🇨🇺','Cuba'], 371:['🇱🇻','Latvia'], 370:['🇱🇹','Lithuania'], 252:['🇸🇴','Somalia'], 224:['🇬🇳','Guinea'], 220:['🇬🇲','Gambia'], 960:['🇲🇻','Maldives'], 372:['🇪🇪','Estonia'], 263:['🇿🇼','Zimbabwe'], 216:['🇹🇳','Tunisia'], 234:['🇳🇬','Nigeria'], 44:['🇬🇧','United Kingdom'], 63:['🇵🇭','Philippines'] };
    const TWO_WAY = { 43:['🇦🇹','Austria'], 32:['🇧🇪','Belgium'], 1:['🇺🇸','United States'], 56:['🇨🇱','Chile'], 420:['🇨🇿','Czech Republic'], 45:['🇩🇰','Denmark'], 358:['🇫🇮','Finland'], 852:['🇭🇰','Hong Kong'], 36:['🇭🇺','Hungary'], 972:['🇮🇱','Israel'], 31:['🇳🇱','Netherlands'], 48:['🇵🇱','Poland'], 351:['🇵🇹','Portugal'], 65:['🇸🇬','Singapore'], 421:['🇸🇰','Slovakia'], 46:['🇸🇪','Sweden'], 41:['🇨🇭','Switzerland'] };
    const OTHER = { 33:['🇫🇷','France'], 49:['🇩🇪','Germany'], 39:['🇮🇹','Italy'], 34:['🇪🇸','Spain'], 52:['🇲🇽','Mexico'], 55:['🇧🇷','Brazil'], 61:['🇦🇺','Australia'], 64:['🇳🇿','New Zealand'], 81:['🇯🇵','Japan'], 91:['🇮🇳','India'] };
    const find = (map) => { for (const L of [3,2,1]) { const c = parseInt(digits.slice(0, L), 10); if (map[c]) return [c, map[c]]; } return null; };
    let hit = find(BLOCKED); if (hit) return setCov({ country_code: hit[0], flag: hit[1][0], country_name: hit[1][1], blocked: true, two_way: false, reason: `SMS to ${hit[1][1]} isn't supported — use email for this recipient.` });
    hit = find(TWO_WAY); if (hit) return setCov({ country_code: hit[0], flag: hit[1][0], country_name: hit[1][1], blocked: false, two_way: true, reason: 'Replies enabled.' });
    hit = find(OTHER); if (hit) return setCov({ country_code: hit[0], flag: hit[1][0], country_name: hit[1][1], blocked: false, two_way: false, reason: 'Send-only — replies may not reach you.' });
    setCov({ country_code: null, flag: '🌐', country_name: 'International', blocked: false, two_way: false, reason: 'Send-only — replies may not reach you.' });
  }, [phone]);
  return cov;
}
